// Support (contracts/messaging.md §4): an inbox-style chat between a player and the team. Every role reads;
// editors reply, close and reopen. Opening a conversation marks it read for the team.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "./client";

export const SUPPORT_FILTERS = ["open", "closed", "all"] as const;
export type SupportFilter = (typeof SUPPORT_FILTERS)[number];
export type SupportStatus = Exclude<SupportFilter, "all">;
export const REPLY_MAX = 2000;

const count = z.number().int().min(0);
const Sender = z.enum(["player", "team"]);
const Status = z.enum(["open", "closed"]);

export const SupportThreadItem = z.object({
  userId: z.string(),
  handle: z.string().nullable(),
  status: Status,
  /** `body`: the first 140 characters. */
  lastMessage: z.object({ sender: Sender, body: z.string(), createdAt: z.string() }),
  teamUnread: count,
  pro: z.boolean(),
  locale: z.string().nullable(),
});
export type SupportThreadItem = z.infer<typeof SupportThreadItem>;
export const SupportThreads = z.object({ items: z.array(SupportThreadItem), unread: count });

export const SupportMessage = z.object({ id: z.string(), sender: Sender, body: z.string(), createdAt: z.string(), adminEmail: z.string().nullable() });
export type SupportMessage = z.infer<typeof SupportMessage>;

export const SupportThreadDetail = z.object({
  thread: z.object({ status: Status, createdAt: z.string(), lastMessageAt: z.string(), closedAt: z.string().nullable() }),
  messages: z.array(SupportMessage),
  player: z.object({
    userId: z.string(),
    handle: z.string().nullable(),
    pro: z.boolean(),
    locale: z.string().nullable(),
    createdAt: z.string(),
    streak: count,
    signedIn: z.boolean(),
  }),
});
export type SupportThreadDetail = z.infer<typeof SupportThreadDetail>;
export const SupportSummary = z.object({ unread: count });

export const supportApi = {
  list: (status: SupportFilter, q: string, signal?: AbortSignal) => request(SupportThreads, "GET", "/support", { query: { status, q }, signal }),
  summary: (signal?: AbortSignal) => request(SupportSummary, "GET", "/support/summary", { signal }),
  thread: (userId: string, signal?: AbortSignal) => request(SupportThreadDetail, "GET", `/support/${encodeURIComponent(userId)}`, { signal }),
  reply: (userId: string, body: string) => request(SupportMessage, "POST", `/support/${encodeURIComponent(userId)}/messages`, { body: { body } }),
  setStatus: (userId: string, status: SupportStatus) => request(z.null(), "PATCH", `/support/${encodeURIComponent(userId)}`, { body: { status } }),
};

export const supportKeys = {
  all: ["support"] as const,
  lists: ["support", "list"] as const,
  list: (status: SupportFilter, q: string) => ["support", "list", status, q] as const,
  summary: ["support", "summary"] as const,
  thread: (userId: string) => ["support", "thread", userId] as const,
};

/** The thread list refreshes every 30 s, so new conversations show up. */
export const useSupportThreads = (status: SupportFilter, q: string) =>
  useQuery({
    queryKey: supportKeys.list(status, q),
    queryFn: ({ signal }) => supportApi.list(status, q, signal),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

/** The nav badge: conversations waiting on the team, every 30 s. */
export const useSupportSummary = (enabled: boolean) =>
  useQuery({ queryKey: supportKeys.summary, queryFn: ({ signal }) => supportApi.summary(signal), enabled, refetchInterval: 30_000 });

/** The open conversation, every 15 s (the app polls at the same pace). Each fetch marks it read. */
export const useSupportThread = (userId: string | undefined) =>
  useQuery({
    queryKey: supportKeys.thread(userId ?? ""),
    queryFn: ({ signal }) => supportApi.thread(userId!, signal),
    enabled: Boolean(userId),
    refetchInterval: 15_000,
  });

function useSupportMutation<V>(fn: (v: V) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: supportKeys.all });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export const useSupportReply = () => useSupportMutation(({ userId, body }: { userId: string; body: string }) => supportApi.reply(userId, body));
export const useSupportStatus = () => useSupportMutation(({ userId, status }: { userId: string; status: SupportStatus }) => supportApi.setStatus(userId, status));
